import { Box, Button, Card, Section } from '@factorialco/deprecated-design-system'
import { useToggle } from 'usehooks-ts'

import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import type Company from 'modules/core/store/models/Company'

import { useAtsEnabled } from 'modules/ats/lib/atsEnabled'
import { CompanyPageUrlForm } from 'modules/company_page/components/CompanyPageUrlForm/CompanyPageUrlForm'
import { useCompanyPageStatusToggle } from 'modules/company_page/hooks/useCompanyPageStatusToggle'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

interface Props {
  company: Company
  domains: string[]
}

export const CompanyPage = ({ company, domains }: Props) => {
  const { atsEnabled } = useAtsEnabled()
  const currentCompany = companies.current
  const { running, isInstalled, isConfigured, toggleAddonStatus, needsPlanUpgrade } =
    useCompanyPageStatusToggle()
  const [upgradeModal, toggleUpgradeModal] = useToggle(false)

  const openCompanyPage = () => {
    const protocol = window.location.protocol
    const companyUrl = `${currentCompany.slug}.${currentCompany.domain}`
    window.open(`${protocol}//${companyUrl}`, '_blank')
  }
  const isToggleDisabled = running || (isInstalled && atsEnabled) || needsPlanUpgrade

  return (
    <Box>
      <Section
        illustration="URL"
        title={i18n.t('company_settings.general.company_page.title')}
        description={i18n.t(
          'company_settings.general.company_page.options.company_page.description'
        )}
        action={
          isInstalled && isConfigured
            ? {
                label: i18n.t(
                  'company_settings.general.company_page.options.company_page.open_company_page_button'
                ),
                onClick: openCompanyPage,
              }
            : undefined
        }
      >
        <Card>
          <Card.Title size="small">
            {i18n.t(
              'company_settings.general.company_page.options.company_page.publish_form_title'
            )}
          </Card.Title>
          <Card.Description>
            {i18n.t(
              'company_settings.general.company_page.options.company_page.publish_form_description'
            )}
          </Card.Description>
          <Card.Content>
            {needsPlanUpgrade && (
              <Button onClick={toggleUpgradeModal}>
                {i18n.t(
                  'company_settings.general.company_page.options.company_page.plan_upgrade_button'
                )}
              </Button>
            )}
          </Card.Content>
          <Card.ExpandableContent
            expanded={isInstalled}
            disabled={isToggleDisabled}
            onExpandChange={toggleAddonStatus}
          >
            <CompanyPageUrlForm company={company} domains={domains} />
          </Card.ExpandableContent>
        </Card>
        {upgradeModal && (
          <RequestUpgradeModal
            requestReason={ValidReasons.UpgradeFromLegacy}
            requestSource="company-page-settings"
            onClose={toggleUpgradeModal}
          />
        )}
      </Section>
    </Box>
  )
}
